// Snip-mode processors. Faithful port of the original SnippetProcessingService:
//   Text       -> raw extracted text into the cell
//   Sum        -> pull numbers out of the text, build =a+b+c
//   Table      -> split into a 2D grid, write a block with borders
//   Validation -> a check mark
//   Exception  -> a cross
import type { SnipMode, WritePlan } from '../types'

export const MODE_LABELS: Record<SnipMode, string> = {
  text: 'Text',
  sum: 'Sum',
  table: 'Table',
  validation: 'Validation',
  exception: 'Exception',
}

// Cell fills — exact match to the original add-in's System.Drawing named colours:
// Text=LightBlue, Sum=LightYellow, Table=Thistle, Validation=LightGreen, Exception=Salmon.
export const MODE_COLORS: Record<SnipMode, string> = {
  text: '#ADD8E6', // LightBlue
  sum: '#FFFFE0', // LightYellow
  table: '#D8BFD8', // Thistle
  validation: '#90EE90', // LightGreen
  exception: '#FA8072', // Salmon
}

export function cleanText(text: string): string {
  return text.replace(/\r/g, '').replace(/[ \t]+\n/g, '\n').trim()
}

/** Parse a single numeric token honouring EU (1.234,56) vs US (1,234.56) notation. */
export function parseNumber(raw: string, eu: boolean): number {
  let s = raw.trim()
  if (eu) {
    // dots are thousands separators, comma is the decimal point
    s = s.replace(/\./g, '').replace(',', '.')
  } else {
    // commas are thousands separators, dot is the decimal point
    s = s.replace(/,/g, '')
  }
  const n = Number.parseFloat(s)
  return Number.isFinite(n) ? n : Number.NaN
}

/** Pull every number out of free text, in reading order.
 *  Tokens never span whitespace — otherwise a column like "100\n200\n300" would
 *  collapse into a single concatenated number (and Sum would write 100200300). */
export function extractNumbers(text: string, eu: boolean): number[] {
  const tokens = text.match(/-?\d[\d.,]*\d|-?\d/g) ?? []
  const out: number[] = []
  for (const t of tokens) {
    const n = parseNumber(t, eu)
    if (Number.isFinite(n)) out.push(n)
  }
  return out
}

function looksNumeric(cell: string, eu: boolean): boolean {
  if (!cell.trim()) return false
  return Number.isFinite(parseNumber(cell, eu))
}

/** Turn extracted text into a 2D grid. Columns are inferred from tabs / 2+ spaces. */
export function parseTable(text: string, eu: boolean): (string | number)[][] {
  const lines = cleanText(text)
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0)

  // Split each row on tabs or runs of 2+ spaces; keep empty interior cells so columns
  // stay aligned. Numeric-looking cells become real numbers.
  const rows = lines.map((line) =>
    line
      .split(/\t|\s{2,}/)
      .map((c) => c.trim())
      .map((c) => (looksNumeric(c, eu) ? parseNumber(c, eu) : c)),
  )

  return rows.length ? rows : [['']]
}

export function buildPlan(mode: SnipMode, rawText: string, eu: boolean): WritePlan {
  switch (mode) {
    case 'text':
      return { mode, kind: 'value', value: cleanText(rawText) }
    case 'sum': {
      const nums = extractNumbers(rawText, eu)
      if (nums.length === 0) return { mode, kind: 'skip', message: 'No numbers found in the selection' }
      if (nums.length === 1) return { mode, kind: 'value', value: nums[0] }
      return { mode, kind: 'formula', formula: '=' + nums.join('+') }
    }
    case 'table':
      return { mode, kind: 'matrix', matrix: parseTable(rawText, eu) }
    case 'validation':
      return { mode, kind: 'value', value: '✔' }
    case 'exception':
      return { mode, kind: 'value', value: 'X' }
  }
}
